import { Component, Input } from "@angular/core";
import { Router } from "@angular/router";
import { CurrentData } from "src/app/shared/components/edge/currentdata";
import { Service } from "src/app/shared/shared";
import { DefaultTypes } from "src/app/shared/type/defaulttypes";

/** Below this power (in W), a link is considered idle and its flow animation is hidden. */
const ACTIVE_POWER_THRESHOLD_WATT = 49;

type FlowDirection = "toHub" | "fromHub";

export interface FlowNode {
    id: "production" | "grid" | "storage";
    icon: string;
    label: string;
    accent: "production" | "grid" | "storage";
    value: number;
    subText: string | null;
    active: boolean;
    direction: FlowDirection;
    /** Animation duration in seconds - faster for higher power. */
    duration: number;
    path: string;
    cx: number;
    cy: number;
    r: number;
}

@Component({
    selector: "energymonitor-flow",
    templateUrl: "./energymonitor-flow.component.html",
    styleUrls: ["./energymonitor-flow.component.scss"],
    standalone: false,
})
export class EnergymonitorFlowComponent {

    protected readonly viewBoxSize = 320;
    protected readonly viewBox = "0 0 320 320";
    protected readonly hub = { cx: 232, cy: 160, r: 62 };

    protected hubValue = 0;
    protected hubActive = false;
    protected nodes: FlowNode[] = [];

    constructor(
        private router: Router,
        private service: Service,
    ) { }

    @Input()
    set currentData(currentData: CurrentData) {
        this.update(currentData.summary);
    }

    protected pct(value: number): number {
        return (value / this.viewBoxSize) * 100;
    }

    /** Navigates to the Monitoring tab (Energiefluss-Übersicht inkl. Sankey-Diagramm). */
    protected navigateToMonitoring(): void {
        const edge = this.service.currentEdge();
        if (edge == null) {
            return;
        }
        this.router.navigate(["/device/" + edge.id + "/energyflux"]);
    }

    /** Fractional animation-delay (in seconds) for the n-th trailing dot on a link. */
    protected trailDelay(duration: number, index: number): number {
        return -(duration / 3) * index;
    }

    private update(sum: DefaultTypes.Summary): void {
        this.hubValue = sum.consumption.activePower ?? 0;
        this.hubActive = this.hubValue > ACTIVE_POWER_THRESHOLD_WATT;

        const production = sum.production.activePower ?? 0;
        const gridBuy = sum.grid.buyActivePower ?? 0;
        const gridSell = sum.grid.sellActivePower ?? 0;
        const storageCharge = sum.storage.effectiveChargePower ?? 0;
        const storageDischarge = sum.storage.effectiveDischargePower ?? 0;

        this.nodes = [
            {
                id: "production",
                icon: "oe-production",
                label: "GENERAL.PRODUCTION",
                accent: "production",
                value: production,
                subText: null,
                active: production > ACTIVE_POWER_THRESHOLD_WATT,
                direction: "toHub",
                duration: this.durationFor(production),
                path: "M137,99 C175,90 150,145 180,127",
                cx: 98, cy: 74, r: 46,
            },
            {
                id: "grid",
                icon: "oe-grid",
                label: "GENERAL.GRID",
                accent: "grid",
                value: gridBuy > 0 ? gridBuy : gridSell,
                subText: null,
                active: (gridBuy > ACTIVE_POWER_THRESHOLD_WATT) || (gridSell > ACTIVE_POWER_THRESHOLD_WATT),
                direction: gridSell > ACTIVE_POWER_THRESHOLD_WATT ? "fromHub" : "toHub",
                duration: this.durationFor(gridBuy > 0 ? gridBuy : gridSell),
                path: "M96,160 C135,150 135,170 170,160",
                cx: 50, cy: 160, r: 46,
            },
            {
                id: "storage",
                icon: "oe-storage",
                label: "EDGE.INDEX.ENERGYMONITOR.STORAGE",
                accent: "storage",
                value: storageCharge > 0 ? storageCharge : storageDischarge,
                subText: sum.storage.soc != null ? Math.round(sum.storage.soc) + " %" : null,
                active: (storageCharge > ACTIVE_POWER_THRESHOLD_WATT) || (storageDischarge > ACTIVE_POWER_THRESHOLD_WATT),
                direction: storageDischarge > ACTIVE_POWER_THRESHOLD_WATT ? "toHub" : "fromHub",
                duration: this.durationFor(storageCharge > 0 ? storageCharge : storageDischarge),
                path: "M137,221 C175,230 150,175 180,194",
                cx: 98, cy: 246, r: 46,
            },
        ];
    }

    /** Scales flow-dot speed with power: more power -> shorter loop duration. */
    private durationFor(absPower: number): number {
        const clamped = Math.min(Math.max(absPower, 0), 10000);
        return 2.6 - (clamped / 10000) * 1.6;
    }
}
